'use client';

import React, { useMemo } from 'react';
import { useGetTopProvidersQuery } from 'src/api/redux';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { buildTopProvidersQueryParams } from './utils';

const PREVIEW_MAX_ITEMS = 2;

const CompactTopProvidersPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  const filterSet = widgetConfig?.filterSet ?? null;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const providerFilterSetId = widgetConfig?.providerFilterSetId;
  const encounterId = widgetConfig?.widget?.encounter?.id ?? null;

  const queryParams = useMemo(
    () => buildTopProvidersQueryParams(filterSet, minYear, maxYear, providerFilterSetId, encounterId),
    [filterSet, minYear, maxYear, providerFilterSetId, encounterId]
  );

  const { data, isLoading } = useGetTopProvidersQuery(
    { providerId, queryParams },
    { skip: !providerId || !profileType }
  );

  const providers = useMemo(() => {
    if (!data?.results) return [];
    return data.results.slice(0, PREVIEW_MAX_ITEMS);
  }, [data]);

  if (isLoading) {
    return <WidgetLoading data-testid="compact-top-providers-preview-loading" aria-label="Loading top providers" />;
  }

  if (providers.length === 0) {
    return <WidgetEmpty data-testid="compact-top-providers-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="compact-top-providers-preview"
      role="list"
      aria-label="Top Providers"
    >
      {providers.map((provider: Record<string, any>, index: number) => (
        <div
          key={`${provider.provider_id}-${index}`}
          className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-b-0"
          role="listitem"
        >
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-medium text-primary truncate">
              {provider.name || 'N/A'}
            </span>
            {provider.specialty && (
              <span className="text-xs text-secondary truncate">
                {provider.specialty}
              </span>
            )}
          </div>
          <div className="flex flex-col items-end flex-shrink-0 ml-2 bg-teal-50 rounded px-2 py-1 w-20 min-w-20">
            <span className="text-sm font-semibold text-teal-900">
              {provider.claims
                ? provider.claims === '<11'
                  ? provider.claims
                  : Number(provider.claims).toLocaleString()
                : '\u2014'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompactTopProvidersPreview;
